import React from "react";
import { useNavigate } from "react-router-dom";
import { buildVisitNavigation } from "../utils/visitPathUtils";
import { canAccessPage } from "../config/roles";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const AVATAR_COLORS = ["#14b8a6","#3b82f6","#8b5cf6","#f43f5e","#f59e0b","#10b981"];

const avatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + hash * 31;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const calcAge = (dob) => {
  if (!dob) return null;
  try {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
};

const typeLabel = (type = "") => {
  // `type` can be explicitly `null` (e.g. mapRosterRowToPatient sets it to
  // null for patient-level roster rows with no visit type) — a default
  // parameter only covers `undefined`, so guard null here too.
  const t = (type || "").trim().toUpperCase();
  if (!t) return null;
  // normalise common variants
  if (t === "FOLLOW UP" || t === "FOLLOW-UP" || t === "F/U" || t === "FU")
    return "F/U";
  if (t === "NEW" || t === "NEW PATIENT") return "NEW";
  return t;
};

const TypeBadge = ({ type }) => {
  const label = typeLabel(type);
  if (!label) return null;
  const isNew = label === "NEW";
  return (
    <span
      className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
        isNew
          ? "bg-emerald-100 text-emerald-700"
          : "bg-blue-100 text-blue-700"
      }`}
    >
      {label}
    </span>
  );
};

const LeejoeSchedulesView = ({
  schedules = [],
  selectedDate,
  userRole,
  // Selection — same contract as SchedulesTable: selectedRows is { [id]: true },
  // onSelectedRowChange receives { selection, scheduleData }.
  selectedRows = {},
  onSelectedRowChange = null,
  // Roster-page reuse — all optional, defaults keep Schedules behavior.
  onPatientClick = null,
  title = "Today’s clinic",
  unitLabel = "visit",
  hideStats = false,
  // Override the stat tiles ({icon, value, label}[]) while keeping their
  // exact design — the roster page shows patient counts instead of the
  // schedule-day stats. Default (null) keeps the Schedules tiles.
  stats = null,
  // Cross-schema providers — the viewer's own schema. Used only to decide
  // whether a row's `_schema` tag is "foreign" (renders a provider chip).
  // Default (null) keeps today's behavior: no chip ever renders.
  homeSchema = null,
  // Per-row action chips (Schedules passes ScheduleRowActionsHost's
  // renderer). Rendered at the right end of each row; clicks and right-clicks
  // inside never reach the row, so they don't open the chart or toggle
  // selection. Default (null) renders nothing, so the roster page is unchanged.
  renderRowActions = null,
}) => {
  const navigate = useNavigate();
  const selectable = typeof onSelectedRowChange === "function";

  const openPatient = (row) => {
    if (typeof onPatientClick === "function") {
      onPatientClick(row);
      return;
    }
    // Shared with the search results table, which had drifted from this and
    // was sending neither the date nor the schema.
    const nav = buildVisitNavigation(
      row,
      canAccessPage(userRole, "clinicPage") ? "clinic" : "visit",
      selectedDate || row.date_,
    );
    if (!nav) return;
    navigate(nav.to, { state: nav.state });
  };

  const applySelection = (selection, scheduleData) => {
    onSelectedRowChange({ selection, scheduleData });
  };

  // Right-click toggles selection: adds the patient to the multi-selection,
  // or removes them if already selected. No menu — selection only.
  const handleRowContextMenu = (e, row) => {
    if (!selectable) return;
    e.preventDefault();
    const id = row.id;
    if (id == null) return;
    if (selectedRows[id]) {
      const next = { ...selectedRows };
      delete next[id];
      applySelection(next, null);
    } else {
      applySelection({ ...selectedRows, [id]: true }, row);
    }
  };

  const dateLabel = selectedDate
    ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).toUpperCase()
    : "";

  const scheduled = schedules.length;
  const needsForms = schedules.filter((s) => !s.forms_ind).length;
  const selectedCount = selectable ? Object.keys(selectedRows).length : 0;

  return (
    <div id="clinic-root" className="min-h-0 bg-white text-slate-900 px-6 py-5">
      {/* Date */}
      {dateLabel && (
        <p className="text-xs font-bold tracking-widest text-blue-600 mb-1">
          {dateLabel}
        </p>
      )}

      {/* Title */}
      <h2 className="text-2xl font-bold mb-4">
        {title}{" "}
        <span className="font-light italic text-slate-500">
          &mdash; {scheduled} {scheduled === 1 ? unitLabel : `${unitLabel}s`}
        </span>
        {selectedCount > 0 && (
          <span className="ml-3 align-middle text-xs font-semibold text-blue-700 bg-blue-100 rounded-full px-2 py-0.5">
            {selectedCount} selected
          </span>
        )}
      </h2>

      {/* Stats row */}
      {!hideStats && (
        <div className="flex flex-wrap gap-3 mb-5">
          {(stats || [
            { icon: "📅", value: scheduled, label: "Scheduled" },
            { icon: "⏱", value: 0, label: "In room now" },
            { icon: "✅", value: 0, label: "Notes done" },
            { icon: "📋", value: needsForms, label: "Needs forms" },
          ]).map(({ icon, value, label }) => (
            <div
              key={label}
              className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-w-[110px]"
            >
              <span className="text-lg">{icon}</span>
              <div>
                <div className="text-xl font-bold leading-tight">{value}</div>
                <div className="text-[11px] text-slate-500">{label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Patient list */}
      {schedules.length === 0 ? (
        <p className="text-slate-400 text-sm py-6 text-center">
          {unitLabel === "visit" ? "No schedule entries for this date." : "No patients found."}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {schedules.map((row, i) => {
            const name = row.patient_name || row.name || "Unknown";
            const initials = getInitials(name);
            const color = avatarColor(name);
            const age = calcAge(row.dob);
            const formatted = row._displayTime || null;
            const hasNotesSigned = !!row.notes_done;
            const isSelected = selectable && row.id != null && !!selectedRows[row.id];

            return (
              <div
                key={row.id ?? i}
                onClick={() => openPatient(row)}
                onContextMenu={(e) => handleRowContextMenu(e, row)}
                className={`flex items-center ${
                  renderRowActions ? "flex-wrap gap-x-4 gap-y-2 sm:flex-nowrap" : "gap-4"
                } rounded-xl px-4 py-3 shadow-sm cursor-pointer transition-colors border ${
                  isSelected
                    ? "bg-blue-50 border-blue-400 ring-1 ring-blue-300"
                    : "bg-white border-slate-200 hover:bg-slate-50"
                }`}
              >
                {/* Time */}
                <div className="w-14 shrink-0 text-right">
                  <div className="text-sm font-semibold leading-tight tabular-nums">
                    {formatted || "—"}
                  </div>
                </div>

                {/* Color bar */}
                <div style={{ width: 4, minHeight: 40, borderRadius: 4, backgroundColor: isSelected ? "#3b82f6" : "#2dd4bf", flexShrink: 0 }} />

                {/* Avatar */}
                <div
                  style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: isSelected ? "#3b82f6" : color, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700 }}
                >
                  {isSelected ? "✓" : initials}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">
                    {name}
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-1.5 items-center mt-0.5">
                    {age !== null && <span>{age}y</span>}
                    {row.EHR_ID && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span>{row.EHR_ID}</span>
                      </>
                    )}
                    {hasNotesSigned && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-slate-400">Note signed</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Foreign-provider chip — only rows a cross-schema fan-out
                    pulled in from a schema other than the viewer's own. */}
                {row._schema && homeSchema && row._schema !== homeSchema && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">{row._schema}</span>
                )}

                {/* Type badge */}
                <TypeBadge type={row.type} />

                {/* Row actions — far right; drops to its own line on phones. */}
                {renderRowActions && (
                  <div
                    data-testid="row-actions"
                    className="flex w-full shrink-0 justify-end sm:w-auto sm:border-l sm:border-slate-200 sm:pl-3"
                    onClick={(e) => e.stopPropagation()}
                    onContextMenu={(e) => e.stopPropagation()}
                  >
                    {renderRowActions(row)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default LeejoeSchedulesView;
